import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import {
	AuCommentDeletePayload,
	AuCommentEditMutationPayload,
	IAuComment,
	AuCommentMutationPayload,
} from '@shared/ui-components/au-comments/i-au.comment';
import {
	AddCommentToEntity,
	DeleteComment,
	UpdateComment,
} from '@app/pages/cmms/graphql/cmms-comments.mutations';
import { map, take } from 'rxjs/operators';
import { ICMMSEntities } from '@app/pages/cmms/interfaces/cmms-global.interfaces';
import { AuErrorAdapter } from '@app/shared';
import { IconSnackBarService } from '@core/services/icon-snack-bar.service';

@Injectable({ providedIn: 'root' })
export class CmmsCommentsService {
	constructor(
		private apollo: Apollo,
		private notify: IconSnackBarService
	) {}

	addCommentToEntity(
		entityType: 'workOrder' | 'incident' | 'request' | 'file',
		primaryKey: string,
		comment: {
			commentText: string;
			sendAsEmail: boolean;
			mentions: string[];
		}
	): Observable<{
		comment: IAuComment;
		success: boolean;
	}> {
		return this.apollo
			.mutate<AuCommentMutationPayload>({
				mutation: AddCommentToEntity,
				fetchPolicy: 'network-only',
				variables: {
					objectId: primaryKey,
					objectType: entityType,
					text: comment.commentText,
					sendAsEmail: comment.sendAsEmail,
					mentions: comment.mentions,
				},
			})
			.pipe(
				take(1),
				map(value => value?.data?.createComment)
			);
	}

	deleteComment(commentId: string): Observable<{ success: boolean }> {
		return this.apollo
			.mutate<AuCommentDeletePayload>({
				mutation: DeleteComment,
				fetchPolicy: 'network-only',
				variables: {
					commentId,
				},
			})
			.pipe(
				take(1),
				map(value => value?.data?.deleteComment)
			);
	}

	editComment(
		commentId: string,
		commentText: string
	): Observable<{
		comment: IAuComment;
		success: boolean;
	}> {
		return this.apollo
			.mutate<AuCommentEditMutationPayload>({
				mutation: UpdateComment,
				fetchPolicy: 'network-only',
				variables: {
					commentId,
					text: commentText,
				},
			})
			.pipe(
				take(1),
				map(({ data }) => data?.updateComment)
			);
	}

	editEntityComment(
		commentId: string,
		commentText: string,
		entityItem: ICMMSEntities,
		entityUpdateCallbackFn: (updatedEntityItem: ICMMSEntities) => void
	) {
		this.editComment(commentId, commentText).subscribe({
			next: result => {
				if (!result.success) {
					this.notify.error('Error: Cannot edit comment');
					return;
				}

				const entityCopy = structuredClone(entityItem);
				entityCopy.comments = entityCopy.comments.map(comment =>
					comment.id === commentId ? { ...comment, text: commentText } : comment
				);

				entityUpdateCallbackFn(entityCopy);
			},
			error: error => {
				this.notify.error(AuErrorAdapter.getTextFromGqlError(error));
			},
		});
	}
}
